import { useEffect, useState, type RefObject } from "react";

/**
 * Tracks a `<video>` element's playhead position and exposes it
 * as React state + a formatted `MM:SS` / `H:MM:SS` string.
 *
 * Why a hook: Coach Review needs the live `currentTime` in two
 * places — the time readout in the header bar, and the save
 * handler (a video note's `timestamp_seconds` is the playhead
 * position at save time; M4 hardcoded `0` because there was no
 * player — the M4 parity-audit P0-2 finding). Reading
 * `video.currentTime` directly in a render is non-reactive; this
 * hook subscribes to `timeupdate` / `seeked` / `loadedmetadata`
 * and re-renders the consumer when the position changes.
 *
 * `timeupdate` fires ~4×/s during playback (browser-throttled —
 * cheap enough to drive a text readout). `seeked` covers scrub
 * jumps; `loadedmetadata` gives the initial 0:00 once the source
 * is ready. The listeners are (re)attached whenever the ref's
 * element identity changes — the consumer passes a stable ref,
 * but the element behind it can swap (e.g. `<HlsVideo>` remounts
 * on a match change), so the hook re-checks on every render via
 * the ref-current dependency.
 *
 * Returns:
 *   - `currentTime` — seconds, or `null` when no video / not
 *     loaded yet (so the caller can render `--:--` and NOT save a
 *     note at a bogus 0).
 *   - `formatted` — `MM:SS` (or `H:MM:SS` past an hour), or
 *     `"--:--"` when `currentTime` is null.
 */

export type VideoPlayhead = {
  currentTime: number | null;
  formatted: string;
};

/** Format seconds as `MM:SS`, or `H:MM:SS` once past an hour.
 * Mirrors legacy `_renderCoachReviewTime`. */
export function formatPlayhead(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return "--:--";
  }
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const ss = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}

export function useVideoPlayhead(
  videoRef: RefObject<HTMLVideoElement | null>,
): VideoPlayhead {
  const [currentTime, setCurrentTime] = useState<number | null>(null);
  // The element behind the ref can change identity (HlsVideo
  // remounts on match change). Track it in state so the effect
  // re-runs and re-attaches listeners when it does.
  const el = videoRef.current;

  useEffect(() => {
    if (!el) {
      setCurrentTime(null);
      return;
    }
    const sync = () => {
      const t = el.currentTime;
      setCurrentTime(Number.isFinite(t) && t >= 0 ? t : null);
    };
    // Seed immediately — metadata may already be loaded if the
    // element was reused.
    sync();
    el.addEventListener("timeupdate", sync);
    el.addEventListener("seeked", sync);
    el.addEventListener("loadedmetadata", sync);
    return () => {
      el.removeEventListener("timeupdate", sync);
      el.removeEventListener("seeked", sync);
      el.removeEventListener("loadedmetadata", sync);
    };
  }, [el]);

  return { currentTime, formatted: formatPlayhead(currentTime) };
}
